// ============================================================
// ALWAYS ON AND SOLO MODES (plan modes-rework, 2026-10-27; "stances"
// before it) - the named bundles of effects a sheet carries.
//
//   alwaysOn   - always up; a TRIGGERED one (`whileOwn`) only while its
//                bearer carries that state (Prone, Stunned...). Nobody
//                switches either.
//   soloModes  - switched on and off by hand. A `group` is "one at a
//                time" (a doctrine): taking one up drops its fellows.
//                EVERY Solo mode stands down in Squad Mode - masked,
//                never cleared, so they come back as they were.
//
// Which Solo modes are switched on is live state, `CombatState.
// activeStances` (the stored key keeps its name). What the bundles hold is
// `effectItems`. An older sheet's stance groups are read into these two
// lists where a sheet comes in (`stanceMigration`).
//
// Pure. Zero React dependency.
// ============================================================
import { OWN_STATE_CHOICES, ownStateHolds, readOwnState } from './effectWhen';
import { readEffects, type StanceEffect } from './effectItems';
import { readOrigin } from '../assets/provenance';
import type { OwnState } from './effectWhen';
import type { Origin } from '../assets/types';

/** An Always on bundle; `whileOwn` makes it a trigger, up only while its bearer carries that state. */
export interface EffectItem {
  id: string;
  name: string;
  effects: StanceEffect[];
  whileOwn?: OwnState;
  /** The library item it was copied from. */
  origin?: Origin;
}

/** A Solo mode: switched by hand; in a `group`, one at a time. */
export interface SoloMode {
  id: string;
  name: string;
  effects: StanceEffect[];
  group?: { id: string; name: string };
  origin?: Origin;
}

/**
 * The two lists on a sheet. READ ONCE where a sheet comes in (`normalizeCharacter`, which `combatantFromCharacter`
 * and `combatantFromDoc` both apply); past that they are trusted as typed.
 */
export interface ModeSheet {
  alwaysOn?: readonly EffectItem[];
  soloModes?: readonly SoloMode[];
}

/** What of a combatant's live state decides what is up. */
export interface ModeState {
  activeStances?: readonly string[] | null;
  conditions?: readonly string[] | null;
  killTeam?: { on?: true } | null;
}

/** One bundle that is up now, with its effects; `key` says which list it came from (the two may share an id). */
export interface Up {
  id: string;
  key: string;
  name: string;
  effects: StanceEffect[];
}

/** The id the free Solo modes are shown under (the strip, the drum, the Effects tab). */
export const FREE_SOLO = 'solo-modes';

const MAX_NAME = 40;
const text = (value: unknown, max = MAX_NAME): string => (typeof value === 'string' ? value.trim().slice(0, max) : '');

type Raw = Record<string, unknown>;

/** The id, name, effects and origin every bundle has; null without an id or a name, or with an id already used. */
function readBundle(raw: unknown, fresh: (id: string) => boolean) {
  const stored = (raw ?? {}) as Raw;
  const id = text(stored.id, 80);
  const name = text(stored.name);
  if (!id || !name || !fresh(id)) return null;
  const origin = readOrigin(stored.origin);
  return { stored, bundle: { id, name, effects: readEffects(stored.effects), ...(origin ? { origin } : {}) } };
}

/** A list read entry by entry, ids kept unique within it. */
function readList<T>(raw: unknown, one: (entry: unknown, fresh: (id: string) => boolean) => T | null): T[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const fresh = (id: string) => !seen.has(id) && !!seen.add(id);
  return raw.map((entry) => one(entry, fresh)).filter((x): x is T => x !== null);
}

/** The stored Always on items, validated. */
export const readAlwaysOn = (raw: unknown): EffectItem[] =>
  readList(raw, (entry, fresh) => {
    const read = readBundle(entry, fresh);
    if (!read) return null;
    const whileOwn = readOwnState(read.stored.whileOwn);
    return whileOwn ? { ...read.bundle, whileOwn } : read.bundle;
  });

/** A stored group: an id and a name, or none. */
function readGroup(raw: unknown): SoloMode['group'] | null {
  const stored = (raw ?? null) as Raw | null;
  const id = text(stored?.id, 80);
  const name = text(stored?.name);
  // The free modes' caption id is never a group's: `soloGroups` would fold the group into the free ones.
  return id && name && id !== FREE_SOLO ? { id, name } : null;
}

/** The stored Solo modes, validated. */
export const readSoloModes = (raw: unknown): SoloMode[] =>
  readList(raw, (entry, fresh) => {
    const read = readBundle(entry, fresh);
    if (!read) return null;
    const group = readGroup(read.stored.group);
    return group ? { ...read.bundle, group } : read.bundle;
  });

/** The Solo modes switched on, in the modes' order (an id that names none is left out). */
function soloSwitched(modes: readonly SoloMode[], active: readonly string[] | null | undefined): SoloMode[] {
  const wanted = new Set(active ?? []);
  return modes.filter((m) => wanted.has(m.id));
}

/** The switched-on ids that still name one of these Solo modes, in the modes' order. */
export const soloOn = (modes: readonly SoloMode[], active: readonly string[] | null | undefined): string[] =>
  soloSwitched(modes, active).map((m) => m.id);

/** Switch one Solo mode: off if on; on, dropping the fellows of its group. An unknown id changes nothing. */
export function toggleSolo(
  modes: readonly SoloMode[],
  active: readonly string[] | null | undefined,
  id: string,
): string[] {
  const now = soloOn(modes, active);
  const mode = modes.find((m) => m.id === id);
  if (!mode) return now;
  if (now.includes(id)) return now.filter((x) => x !== id);
  const fellows = new Set(mode.group ? modes.filter((m) => m.group?.id === mode.group!.id).map((m) => m.id) : []);
  return soloOn(modes, [...now.filter((x) => !fellows.has(x)), id]);
}

/** The Always on items up: every one without a trigger, and each triggered one whose state is carried. */
const alwaysUp = (items: readonly EffectItem[], conditions: readonly string[] | null | undefined) =>
  items.filter((item) => !item.whileOwn || ownStateHolds(item.whileOwn, conditions));

/**
 * WHAT IS UP NOW: the Always on items that stand, then the Solo modes switched on - none of them in Squad Mode.
 * Keys are what a cache key may be built of.
 */
export function upNow(sheet: ModeSheet, state: ModeState | null | undefined): Up[] {
  const always = alwaysUp(sheet.alwaysOn ?? [], state?.conditions).map((a) => up(a, 'a'));
  // Nothing switched on (the usual case) or in Squad Mode: the Solo modes are not even read.
  const live = state?.activeStances?.length && !state.killTeam?.on;
  const solo = live ? soloSwitched(sheet.soloModes ?? [], state.activeStances) : [];
  return [...always, ...solo.map((m) => up(m, 's'))];
}

const up = ({ id, name, effects }: EffectItem | SoloMode, list: 'a' | 's'): Up => ({
  id,
  key: `${list}:${id}`,
  name,
  effects,
});

/** The effects of what is up, each with the bundle it came from. */
export const upEffects = (up: readonly Up[]): { effect: StanceEffect; source: string }[] =>
  up.flatMap((u) => u.effects.map((effect) => ({ effect, source: u.name })));

/** When a triggered item is up, in words; null for one with no trigger. */
export function triggerSentence(item: Pick<EffectItem, 'whileOwn'>): string | null {
  const state = OWN_STATE_CHOICES.find((s) => s.id === item.whileOwn);
  return state ? `Up while I am ${state.label}` : null;
}

/** The Solo modes as the strip and the drum show them: each one-at-a-time group, then the free ones together. */
export interface SoloGroup {
  id: string;
  name: string;
  oneAtATime: boolean;
  modes: SoloMode[];
}

export function soloGroups(modes: readonly SoloMode[]): SoloGroup[] {
  const groups = new Map<string, SoloGroup>();
  for (const m of modes) {
    const key = m.group?.id ?? FREE_SOLO;
    const group =
      groups.get(key) ??
      groups.set(key, { id: key, name: m.group?.name ?? 'Solo modes', oneAtATime: !!m.group, modes: [] }).get(key)!;
    group.modes.push(m);
  }
  return [...groups.values()];
}
